/**
 * Approver-employee context — same fix shape as `legalEntity.tsx`,
 * but for the `?approver_employee_id=<uuid>` URL param that the
 * approval-queue pages used to require.
 *
 * Resolution order:
 *   1. `?approver_employee_id=<uuid>` URL param (deep links).
 *   2. The id persisted in localStorage from a prior visit / picker
 *      click.
 *   3. The first row returned by
 *      `GET /api/mission-control/active-approvers` (i.e. the
 *      employee with the most pending approvals routed to them
 *      right now — the "default approver" for the demo / single-LE
 *      tenants).
 *
 * Once Clerk-user-to-Employee linkage lands (post-M20), step 3
 * gets replaced by "the logged-in user's employee row." Until
 * then, the context lets HR / a demo user pick from a dropdown
 * in the top bar so the approval queues never require a URL
 * UUID.
 */

import { useQuery } from "@tanstack/react-query";
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";

import { api } from "./api";
import type { ActiveApprover } from "./api";
import { useLegalEntityId } from "./legalEntity";

const _LS_KEY = "solvait.approverEmployeeId";

interface ApproverContextValue {
  /** Resolved employee id. Empty string when no active approvers exist. */
  approverEmployeeId: string;
  /** Full list, for the top-bar picker. */
  activeApprovers: ActiveApprover[];
  /** Explicit user choice — persists to localStorage. */
  setApproverEmployeeId: (id: string) => void;
  isLoading: boolean;
}

const ApproverContext = createContext<ApproverContextValue | null>(null);

export function ApproverProvider({ children }: { children: ReactNode }) {
  const [searchParams] = useSearchParams();
  const urlApprover = searchParams.get("approver_employee_id") ?? "";
  const legalEntityId = useLegalEntityId();

  const [persisted, setPersisted] = useState<string>(() => {
    if (typeof window === "undefined") return "";
    return window.localStorage.getItem(_LS_KEY) ?? "";
  });

  const q = useQuery({
    queryKey: ["active-approvers", legalEntityId || "_all_"],
    queryFn: () =>
      api.missionControlActiveApprovers(legalEntityId || undefined),
    staleTime: 60_000,
    retry: false,
  });

  const approvers = q.data?.approvers ?? [];
  const isLoading = q.isLoading;

  const resolvedId = useMemo(() => {
    if (urlApprover && _isPlausibleUuid(urlApprover)) return urlApprover;
    if (persisted && approvers.some((a) => a.employee_id === persisted)) {
      return persisted;
    }
    if (approvers.length > 0) return approvers[0]!.employee_id;
    return "";
  }, [urlApprover, persisted, approvers]);

  useEffect(() => {
    if (resolvedId && resolvedId !== persisted) {
      try {
        window.localStorage.setItem(_LS_KEY, resolvedId);
      } catch {
        // ignore
      }
      setPersisted(resolvedId);
    }
  }, [resolvedId, persisted]);

  const setApproverEmployeeId = useCallback((id: string) => {
    try {
      window.localStorage.setItem(_LS_KEY, id);
    } catch {
      // ignore
    }
    setPersisted(id);
  }, []);

  const value: ApproverContextValue = {
    approverEmployeeId: resolvedId,
    activeApprovers: approvers,
    setApproverEmployeeId,
    isLoading,
  };

  return (
    <ApproverContext.Provider value={value}>
      {children}
    </ApproverContext.Provider>
  );
}

export function useApproverContext(): ApproverContextValue {
  const ctx = useContext(ApproverContext);
  if (ctx === null) {
    throw new Error(
      "useApproverContext must be used inside <ApproverProvider>",
    );
  }
  return ctx;
}

export function useOptionalApproverContext():
  | ApproverContextValue
  | null {
  return useContext(ApproverContext);
}

/**
 * Convenience hook for pages that just need the approver id.
 * Returns "" while loading or when no active approvers exist.
 */
export function useApproverEmployeeId(): string {
  return useApproverContext().approverEmployeeId;
}

function _isPlausibleUuid(value: string): boolean {
  return /^[0-9a-f-]{32,36}$/i.test(value.trim());
}
